import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'rsuite';
import { isEqual } from 'lodash';
import {
    Grid,
    Divider,
    Dialog,
    Drawer,
} from '@material-ui/core';
import { WarningTwoTone } from '@material-ui/icons';
import {
    ShiftlineSelectionModals,
    BidProcessType,
    ShiftlineSelectionTableType,
    AnchorDay,
    ScheduleSource,
    PeriodType,
    EmployeeReportType,
    BidProfileStatus,
} from '../../../../Enums';
import {
    bidOnBehalfOfEmployee,
    declineOnBehalfBidSelection,
    RESET_BID_SELECTION,
    handleAutoOrderSelected,
    loadEmployeeBehalfShiftlineSelection,
} from '../../../../store/bidSelection';
import { RESET_EMPLOYEE_PREFERENCES_REPORT } from '../../../../store/employeePreferencesReport';
import { RESET_UNAWARDED_SHIFTLINES_REPORT } from '../../../../store/unawardedShiftlinesReport';
import { RESET_EMPLOYEE_BIDDING_RESULTS_REPORT } from '../../../../store/employeeBiddingResultsReport';
import ShiftlineFilterSelectionBar from '../../../../Employee/components/ShiftlineFilterSelectionBar';
import {
    ShiftlineSelectionHeader,
    ShiftlineSelectionConfirmation,
    EmployeeReport,
} from '../../../../components';
import ShiftLineSelectionTable from '../../../../Employee/components/ShiftLineSelectionTable';
import './OnBehalfShiftlineSelection.scss';
import { DAY_COLUMNS_COUNT } from '../../ScheduleSelection/ShiftlineTable/ShiftlineTable';

// eslint-disable-next-line no-unused-vars
/**
 * @param {{
 *  profileId: string,
 *  employeeId: number,
 *  open: boolean,
 *  onClose: Function,
 * }} props
 */
function OnBehalfShiftlineSelection(props) {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'employee' });
    const dispatch = useDispatch();

    const {
        profileId,
        employeeId,
        open,
        onClose = () => { },
    } = props;

    /** @type {ShiftBidding.EmployeeStore} */
    // @ts-ignore
    const {
        // @ts-ignore
        employeeBidProfile, employeeBidSelection, bidProfile, pendingTasks,
    } = useSelector((state) => state);
    const {
        name,
        bidProcessType,
        items,
        anchorDay,
        isRebidAllowed,
        sequentialSchedule,
        dayCount,
        periodType,
        weeklyHours,
        weeklyHoursByDefault,
        lockedEmployeeIds,
        employeeRank,
        isGroupShiftlinesWithSameSchedules
    } = employeeBidProfile;

    const isPreferential = bidProcessType === BidProcessType.Preferential;
    const isSequential = bidProcessType === BidProcessType.Sequential;

    const [autoNumberingType, setAutoNumberingType] = useState('');
    const [showModal, setShowModal] = useState(ShiftlineSelectionModals.None);
    const [showReport, setShowReport] = useState(ShiftlineSelectionModals.None);
    const [isLoadingComplete, setIsLoadingComplete] = useState(false);

    const [initialSelection, setInitialSelection] = useState(employeeBidSelection.employeeSelections);
    const isSelectionDirty = !isEqual(initialSelection, employeeBidSelection.employeeSelections);

    const isRecalculateAwarding = bidProfile.status === BidProfileStatus.Closed;

    const handleSubmit = () => {
        // eslint-disable-next-line max-len
        dispatch(bidOnBehalfOfEmployee(profileId, employeeId, employeeBidSelection.employeeSelections)).then((x) => {
            if (x.name !== 'reject') {
                setInitialSelection(employeeBidSelection.employeeSelections);
                onClose(isRecalculateAwarding);
            }
        });
        setShowModal(ShiftlineSelectionModals.None);
    };
    const handleDecline = () => {
        dispatch(declineOnBehalfBidSelection(profileId, employeeId));
        setInitialSelection([]);
        setShowModal(ShiftlineSelectionModals.None);
        onClose(isRecalculateAwarding);
    };
    const handleAutoOrder = () => {
        dispatch(
            handleAutoOrderSelected(
                employeeBidSelection,
                autoNumberingType,
                employeeBidSelection.filteredItems,
                isGroupShiftlinesWithSameSchedules
            )
        );
        setShowModal(ShiftlineSelectionModals.None);
    };
    const handleBack = () => {
        onClose();
        setShowModal(ShiftlineSelectionModals.None);
        setIsLoadingComplete(false);
        dispatch({ type: RESET_BID_SELECTION });
    };

    const renderShiftlineSelectionTable = () => (
        items.length ? (
            <ShiftLineSelectionTable
                isMultipleSelection={isPreferential}
                daysPerPeriod={DAY_COLUMNS_COUNT}
                type={ShiftlineSelectionTableType.EditSelection}
                externalStoreData={{
                    filteredItems: [],
                    shiftsDictionary: {},
                    anchorDay: AnchorDay.Sunday,
                    scheduleSource: ScheduleSource.ExcelFile,
                    dayCount: 0,
                    periodType: PeriodType.Weeks,
                }}
            />
        ) : (
            <div className="noAvailableScheduleWarningBox">
                <WarningTwoTone
                    className="noAvailableScheduleWarningIcon"
                    color="error"
                />
                <span
                    className="noAvailableScheduleWarningText"
                >
                    No schedule available for bidding.
                </span>
            </div>
        ));

    const handleResetStoreData = () => {
        dispatch({ type: RESET_BID_SELECTION });
        dispatch({ type: RESET_EMPLOYEE_PREFERENCES_REPORT });
        dispatch({ type: RESET_UNAWARDED_SHIFTLINES_REPORT });
        dispatch({ type: RESET_EMPLOYEE_BIDDING_RESULTS_REPORT });
    };

    useEffect(() => {
        handleResetStoreData();
        dispatch(loadEmployeeBehalfShiftlineSelection(profileId, employeeId, handleBack)).then((result) => {
            if (result.payload) {
                setInitialSelection(Object.values(result.payload?.employeeBidSelection?.selection || {}));
                setIsLoadingComplete(true);
            }
        });
    }, [employeeId]);

    return (
        <Drawer
            open={open && isLoadingComplete}
            anchor="right"
            className="obss-drawer"
            onClose={() => onClose()}
        >
            <div className="obss-drawer-content">
                <>
                    <ShiftlineSelectionHeader
                        isOnBehalf
                        isSelectionDirty={isSelectionDirty}
                        setShowModal={setShowModal}
                        setShowReport={setShowReport}
                        handleBack={handleBack}
                    />
                    <Divider />
                    <Grid
                        container
                        className="sl-selection-filters"
                    >
                        <ShiftlineFilterSelectionBar
                            isSelectedButtonHidden={!isPreferential}
                            showAutoOrderButton={isPreferential}
                            setShowAutoOrderModal={(/** @type {boolean} */ isSetVisible) => (
                                isSetVisible
                                    ? setShowModal(ShiftlineSelectionModals.AutoNumbering)
                                    : setShowModal(ShiftlineSelectionModals.None)
                            )}
                            periodType={periodType}
                            dayCount={dayCount}
                            weeklyHours={weeklyHours}
                            weeklyHoursByDefault={weeklyHoursByDefault}
                        />
                    </Grid>
                    {
                        pendingTasks
                            ? (
                                <Loader
                                    size="lg"
                                    content="Loading..."
                                    vertical
                                    className="obss-loading-spinner"
                                />
                            )
                            : renderShiftlineSelectionTable()
                    }
                    <Dialog
                        open={showModal !== ShiftlineSelectionModals.None}
                        onClose={() => { }}
                    >
                        <ShiftlineSelectionConfirmation
                            showModal={showModal}
                            templateToLoadName={name}
                            autoNumberingType={autoNumberingType}
                            setShowModal={setShowModal}
                            handleSubmit={handleSubmit}
                            handleDecline={handleDecline}
                            setAutoNumberingType={setAutoNumberingType}
                            handleAutoOrder={handleAutoOrder}
                            handleBack={handleBack}
                            t={t}
                        />
                    </Dialog>
                    <EmployeeReport
                        profileId={profileId}
                        employeeReport={showReport}
                        onClose={() => setShowReport(EmployeeReportType.None)}
                        dataviewEmployeeData={[]}
                        filteredDataviewEmployeeData={[]}
                        dataviewFields={[]}
                        anchorDay={anchorDay}
                        isRebid={isRebidAllowed}
                        showScheduledDateAndTime={isSequential}
                        sequentialSchedule={sequentialSchedule}
                        dayCount={dayCount}
                        isPeriodTypeDay={periodType === PeriodType.Days}
                        lockedEmployeeIds={lockedEmployeeIds}
                        employeeFilters={[{ rank: employeeRank.rank, employeeId: employeeRank.employeeId, name: '' }]}
                    />
                </>
            </div>
        </Drawer>
    );
}

export default OnBehalfShiftlineSelection;
